import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import Navigation from "@/components/Navigation";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import { 
  MapPin, Calendar, Heart, Users, Settings, 
  Sparkles, Baby, Edit2, ChevronRight, Shield, LogOut,
  Clock, Video, X, AlertTriangle, CalendarX, Briefcase, HelpCircle, MessageCircle, Image
} from "lucide-react";
import { format } from "date-fns";

const Profile = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [circles, setCircles] = useState([]);
  const [registeredEvents, setRegisteredEvents] = useState([]);
  const [bookedSessions, setBookedSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Cancel confirmation dialogs
  const [cancelEventDialog, setCancelEventDialog] = useState({ open: false, event: null });
  const [cancelSessionDialog, setCancelSessionDialog] = useState({ open: false, session: null });
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      const [connectionsRes, circlesRes, eventsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/connections`, { withCredentials: true }),
        axios.get(`${API}/circles`, { withCredentials: true }),
        axios.get(`${API}/events/my-registrations`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/bookings`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      
      setConnections(connectionsRes.data);
      setCircles(circlesRes.data.filter(c => c.is_member));
      setRegisteredEvents(eventsRes.data || []);
      setBookedSessions(bookingsRes.data || []);
    } catch (error) {
      console.error("Profile data error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const handleCancelEvent = async () => {
    if (!cancelEventDialog.event) return;
    setCancelling(true);
    
    try {
      await axios.delete(`${API}/events/${cancelEventDialog.event.event_id}/unregister`, { withCredentials: true });
      toast.success("Event registration cancelled");
      setRegisteredEvents(prev => prev.filter(e => e.event_id !== cancelEventDialog.event.event_id));
      setCancelEventDialog({ open: false, event: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel registration");
    } finally {
      setCancelling(false);
    }
  };

  const handleCancelSession = async () => {
    if (!cancelSessionDialog.session) return;
    setCancelling(true);
    
    try {
      await axios.put(`${API}/bookings/${cancelSessionDialog.session.booking_id}/cancel`, {}, { withCredentials: true });
      toast.success("Session cancelled successfully");
      setBookedSessions(prev => prev.filter(s => s.booking_id !== cancelSessionDialog.session.booking_id));
      setCancelSessionDialog({ open: false, session: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel session");
    } finally {
      setCancelling(false);
    }
  };

  const getPhaseIcon = (phase) => {
    switch (phase) {
      case 'ttc': return <Sparkles className="w-5 h-5" />;
      case 'pregnant': return <Heart className="w-5 h-5" />;
      case 'postpartum': return <Baby className="w-5 h-5" />;
      default: return <Heart className="w-5 h-5" />;
    }
  };

  const getPhaseLabel = (phase) => {
    switch (phase) {
      case 'ttc': return 'Trying to Conceive';
      case 'pregnant': return 'Pregnant';
      case 'postpartum': return 'Postpartum';
      default: return phase;
    }
  };

  const getChildrenLabel = (info) => {
    const labels = {
      'first-time-expecting': 'First-Time Expecting',
      'first-time-mom': 'First-Time Mom',
      'one-child': 'Mom of One',
      'two-children': 'Mom of Two',
      'three-plus': 'Mom of 3+',
      'twins': 'Twin Mom',
      'triplets-plus': 'Triplets or More'
    };
    return labels[info] || info;
  };

  const getWorkingLabel = (status) => {
    const labels = {
      'working-full-time': 'Working Full-Time',
      'working-part-time': 'Part-Time',
      'stay-at-home': 'Stay-at-Home Mom',
      'maternity-leave': 'Maternity Leave',
      'freelance': 'Freelance',
      'looking-for-work': 'Looking for Work',
      'student': 'Student'
    };
    return labels[status] || status;
  };

  const resolveImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('/api/uploads/')) {
      return `${API.replace('/api', '')}${url}`;
    }
    return url;
  };

  const formatEventDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "MMM d, yyyy 'at' h:mm a");
    } catch {
      return dateStr;
    }
  };

  const formatSessionDate = (slot) => {
    if (!slot?.start_time) return "TBD";
    try {
      return format(new Date(slot.start_time), "MMM d, yyyy 'at' h:mm a");
    } catch {
      return slot.start_time;
    }
  };

  return (
    <div className="min-h-screen bg-cream pb-24">
      <Navigation />
      
      {/* Profile Header */}
      <header className="relative">
        <div className="h-32 bg-gradient-to-br from-sage/30 to-clay/20" />
        
        <div className="px-6 -mt-16 max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-end gap-4">
            <Avatar className="h-28 w-28 border-4 border-white shadow-lg" data-testid="profile-avatar">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-sage/20 text-sage-dark text-3xl">
                {user?.name?.[0]}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1 pb-2">
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-2xl font-serif text-sage-darker">{user?.name}</h1>
                {user?.is_admin && (
                  <Badge className="bg-purple-100 text-purple-600">
                    <Shield className="w-3 h-3 mr-1" />
                    Admin
                  </Badge>
                )}
              </div>
              {user?.location && (
                <p className="text-muted-foreground text-sm flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {user.location}
                </p>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <Link to="/profile/edit">
                <Button variant="outline" className="border-sage/30 text-sage-darker hover:bg-sage/10 rounded-full" data-testid="edit-profile-btn">
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit Profile
                </Button>
              </Link>
              <Button 
                variant="outline" 
                onClick={handleLogout}
                className="border-red-200 text-red-500 hover:bg-red-50 hover:text-red-600 rounded-full" 
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="px-6 pt-8 max-w-4xl mx-auto space-y-6" data-testid="profile-main">
        {/* Photo Gallery */}
        {user?.photos && user.photos.length > 0 && (
          <Card className="bg-white border-sage/20" data-testid="photos-section">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-serif text-sage-darker flex items-center gap-2">
                <Image className="w-5 h-5 text-sage" />
                My Photos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3">
                {user.photos.map((photo, idx) => (
                  <div key={idx} className="aspect-square rounded-xl overflow-hidden bg-sage/10">
                    <img 
                      src={resolveImageUrl(photo)} 
                      alt={`Photo ${idx + 1}`} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Phase & Life Details */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-white border-sage/20" data-testid="phase-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  user?.phase === 'ttc' ? 'bg-clay/20 text-clay-dark' :
                  user?.phase === 'pregnant' ? 'bg-sage/20 text-sage-dark' : 'bg-purple-100 text-purple-600'
                }`}>
                  {getPhaseIcon(user?.phase)}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Current Phase</p>
                  <p className="text-sage-darker font-medium">{getPhaseLabel(user?.phase) || "Not set"}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Working Status */}
          <Card className="bg-white border-sage/20" data-testid="working-status-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Work Status</p>
                  <p className="text-sage-darker font-medium">{user?.working_status ? getWorkingLabel(user.working_status) : "Not set"}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Children Info */}
          <Card className="bg-white border-sage/20" data-testid="children-info-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-pink-100 flex items-center justify-center">
                  <Baby className="w-5 h-5 text-pink-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Motherhood</p>
                  <p className="text-sage-darker font-medium">{user?.children_info ? getChildrenLabel(user.children_info) : "Not set"}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-sage/20" data-testid="connections-stat">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-sage/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-sage-dark" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Connections</p>
                  <p className="text-sage-darker font-medium">{connections.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bio & Interests */}
        {(user?.bio || (user?.interests && user.interests.length > 0)) && (
          <Card className="bg-white border-sage/20" data-testid="bio-section">
            <CardContent className="p-6 space-y-4">
              {user?.bio && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">About Me</p>
                  <p className="text-sage-darker leading-relaxed">{user.bio}</p>
                </div>
              )}
              
              {user?.interests && user.interests.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Interests</p>
                  <div className="flex flex-wrap gap-2">
                    {user.interests.map((interest) => (
                      <Badge key={interest} variant="outline" className="border-sage/30 text-sage-darker">
                        {interest}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Looking For Help & Willing to Help */}
        {((user?.looking_for_help && user.looking_for_help.length > 0) || (user?.willing_to_help && user.willing_to_help.length > 0)) && (
          <div className="grid md:grid-cols-2 gap-4">
            {/* Looking for Help */}
            {user?.looking_for_help && user.looking_for_help.length > 0 && (
              <Card className="bg-white border-sage/20" data-testid="looking-for-help-section">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-serif text-sage-darker flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-rose-500" />
                    Looking for Help With
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {user.looking_for_help.map((topic) => (
                      <Badge key={topic} className="bg-rose-100 text-rose-700 border-0">
                        {topic}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Willing to Help */}
            {user?.willing_to_help && user.willing_to_help.length > 0 && (
              <Card className="bg-white border-sage/20" data-testid="willing-to-help-section">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-serif text-sage-darker flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-green-500" />
                    Can Help Others With
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {user.willing_to_help.map((topic) => (
                      <Badge key={topic} className="bg-green-100 text-green-700 border-0">
                        {topic}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Circles Joined */}
        <Card className="bg-white border-sage/20" data-testid="circles-stat">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-serif text-sage-darker flex items-center gap-2">
                <Heart className="w-5 h-5 text-clay" />
                Circles Joined
              </CardTitle>
              <Link to="/circles" className="text-sage-dark text-sm flex items-center gap-1 hover:underline">
                Browse circles <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {circles.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted-foreground text-sm">You have not joined any circles yet</p>
                <Link to="/circles">
                  <Button className="mt-3 bg-sage text-white hover:bg-sage-dark rounded-full" size="sm">
                    Explore Circles
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {circles.map((circle) => (
                  <Link key={circle.circle_id} to={`/circles/${circle.circle_id}`}>
                    <Badge variant="outline" className="border-clay/30 text-clay-dark hover:bg-clay/10 cursor-pointer py-1.5 px-3">
                      {circle.name}
                    </Badge>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Registered Events */}
        <Card className="bg-white border-sage/20" data-testid="registered-events">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-serif text-sage-darker flex items-center gap-2">
                <Calendar className="w-5 h-5 text-sage" />
                My Registered Events
              </CardTitle>
              <Link to="/events" className="text-sage-dark text-sm flex items-center gap-1 hover:underline">
                Browse events <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2].map((i) => (
                  <div key={i} className="h-20 bg-sage/10 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : registeredEvents.length === 0 ? (
              <div className="text-center py-6">
                <Calendar className="w-10 h-10 text-sage/30 mx-auto mb-2" />
                <p className="text-muted-foreground text-sm">No registered events</p>
                <Link to="/events">
                  <Button className="mt-3 bg-sage text-white hover:bg-sage-dark rounded-full" size="sm">
                    Explore Events
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {registeredEvents.map((event) => (
                  <div key={event.event_id} className="flex items-center gap-4 p-4 rounded-xl bg-sage/5 border border-sage/10">
                    <div className="w-14 h-14 rounded-xl bg-sage/20 flex flex-col items-center justify-center flex-shrink-0">
                      <p className="text-sage-dark font-bold text-lg">
                        {new Date(event.scheduled_at).getDate()}
                      </p>
                      <p className="text-sage/70 text-xs">
                        {format(new Date(event.scheduled_at), 'MMM')}
                      </p>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sage-darker font-medium truncate">{event.title}</p>
                      <p className="text-muted-foreground text-sm">
                        {formatEventDate(event.scheduled_at)}
                      </p>
                      <p className="text-xs text-muted-foreground">Host: {event.host_name}</p>
                      {event.meet_link && (
                        <a 
                          href={event.meet_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 mt-1"
                        >
                          <Video className="w-3 h-3" />
                          Join Google Meet
                        </a>
                      )}
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setCancelEventDialog({ open: true, event })}
                      className="border-red-200 text-red-500 hover:bg-red-50 flex-shrink-0"
                    >
                      <CalendarX className="w-4 h-4 mr-1" />
                      Cancel
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Booked Sessions */}
        <Card className="bg-white border-sage/20" data-testid="booked-sessions">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-serif text-sage-darker flex items-center gap-2">
                <Video className="w-5 h-5 text-sage" />
                My Expert Sessions
              </CardTitle>
              <Link to="/experts" className="text-sage-dark text-sm flex items-center gap-1 hover:underline">
                Book a session <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2].map((i) => (
                  <div key={i} className="h-20 bg-sage/10 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : bookedSessions.filter(s => s.status !== 'cancelled').length === 0 ? (
              <div className="text-center py-6">
                <Video className="w-10 h-10 text-sage/30 mx-auto mb-2" />
                <p className="text-muted-foreground text-sm">No booked sessions</p>
                <Link to="/experts">
                  <Button className="mt-3 bg-sage text-white hover:bg-sage-dark rounded-full" size="sm">
                    Browse Experts
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {bookedSessions.filter(s => s.status !== 'cancelled').map((session) => (
                  <div key={session.booking_id} className="flex items-center gap-4 p-4 rounded-xl bg-sage/5 border border-sage/10">
                    <Avatar className="h-12 w-12 flex-shrink-0">
                      <AvatarImage src={session.expert?.picture} />
                      <AvatarFallback className="bg-sage/20 text-sage-dark">
                        {session.expert?.name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sage-darker font-medium truncate">{session.expert?.name}</p>
                      <p className="text-muted-foreground text-sm flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatSessionDate(session.slot)}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className={`text-xs ${
                          session.status === 'confirmed' || session.payment_status === 'completed' 
                            ? 'border-green-300 text-green-600' 
                            : 'border-yellow-300 text-yellow-600'
                        }`}>
                          {session.status === 'confirmed' || session.payment_status === 'completed' ? 'Confirmed' : 'Pending'}
                        </Badge>
                        {session.amount_inr > 0 && (
                          <span className="text-xs text-muted-foreground">₹{session.amount_inr.toLocaleString('en-IN')}</span>
                        )}
                      </div>
                      {session.meet_link && (
                        <a 
                          href={session.meet_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 mt-1"
                        >
                          <Video className="w-3 h-3" />
                          Join Google Meet
                        </a>
                      )}
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setCancelSessionDialog({ open: true, session })}
                      className="border-red-200 text-red-500 hover:bg-red-50 flex-shrink-0"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Cancel
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Connections */}
        <Card className="bg-white border-sage/20" data-testid="connections-section">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-serif text-sage-darker">Connections</CardTitle>
              <Link to="/discover" className="text-sage-dark text-sm flex items-center gap-1 hover:underline">
                Find more <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex gap-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="w-16 h-16 rounded-full bg-sage/10 animate-pulse" />
                ))}
              </div>
            ) : connections.length === 0 ? (
              <p className="text-muted-foreground text-sm">No connections yet</p>
            ) : (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {connections.slice(0, 10).map((conn) => (
                  <div key={conn.request_id} className="flex-shrink-0 text-center">
                    <Avatar className="h-14 w-14 border-2 border-sage/30">
                      <AvatarImage src={conn.connected_user?.picture} />
                      <AvatarFallback className="bg-sage/20 text-sage-dark">
                        {conn.connected_user?.name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <p className="text-xs text-sage-darker mt-1 truncate w-14">
                      {conn.connected_user?.name?.split(' ')[0]}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Admin Link */}
        {user?.is_admin && (
          <Link to="/admin" className="block">
            <Card className="bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200 hover:shadow-md transition-shadow">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-200 rounded-xl flex items-center justify-center">
                    <Settings className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-purple-900 font-medium">Admin Dashboard</p>
                    <p className="text-purple-600 text-sm">Manage users, events & content</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-purple-400" />
              </CardContent>
            </Card>
          </Link>
        )}
      </main>

      {/* Cancel Event Confirmation Dialog */}
      <Dialog open={cancelEventDialog.open} onOpenChange={(open) => setCancelEventDialog({ open, event: cancelEventDialog.event })}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sage-darker">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Cancel Event Registration?
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Are you sure you want to cancel your registration for this event?
            </DialogDescription>
          </DialogHeader>
          
          {cancelEventDialog.event && (
            <div className="p-4 bg-sage/5 rounded-xl my-4">
              <p className="font-medium text-sage-darker">{cancelEventDialog.event.title}</p>
              <p className="text-sm text-muted-foreground">{formatEventDate(cancelEventDialog.event.scheduled_at)}</p>
              {cancelEventDialog.event.price_inr > 0 && (
                <p className="text-sm text-sage-dark mt-2">
                  💰 Refund of ₹{cancelEventDialog.event.price_inr.toLocaleString('en-IN')} will be processed within 5-7 business days.
                </p>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCancelEventDialog({ open: false, event: null })} disabled={cancelling}>
              Keep Registration
            </Button>
            <Button 
              onClick={handleCancelEvent} 
              disabled={cancelling}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              {cancelling ? "Cancelling..." : "Yes, Cancel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Session Confirmation Dialog */}
      <Dialog open={cancelSessionDialog.open} onOpenChange={(open) => setCancelSessionDialog({ open, session: cancelSessionDialog.session })}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sage-darker">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Cancel Expert Session?
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Are you sure you want to cancel this session?
            </DialogDescription>
          </DialogHeader>
          
          {cancelSessionDialog.session && (
            <div className="p-4 bg-sage/5 rounded-xl my-4">
              <p className="font-medium text-sage-darker">Session with {cancelSessionDialog.session.expert?.name}</p>
              <p className="text-sm text-muted-foreground">{formatSessionDate(cancelSessionDialog.session.slot)}</p>
              {cancelSessionDialog.session.amount_paid > 0 && (
                <p className="text-sm text-sage-dark mt-2">
                  💰 Refund of ₹{cancelSessionDialog.session.amount_paid.toLocaleString('en-IN')} will be processed within 5-7 business days.
                </p>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCancelSessionDialog({ open: false, session: null })} disabled={cancelling}>
              Keep Session
            </Button>
            <Button 
              onClick={handleCancelSession} 
              disabled={cancelling}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              {cancelling ? "Cancelling..." : "Yes, Cancel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Profile;
